/**
 * Две вещи, которые до сих пор делались только со стороны сервера: завести Канал и позвать
 * человека в фирму.
 *
 * Кнопки видит Администратор, но держится запрет не на них: сервер откажет и тому, кто
 * позовёт эти адреса руками (§ 2). Спрятанная кнопка — удобство, а не защита.
 *
 * Ссылку-приглашение показываем целиком и текстом, а не одной кнопкой «скопировать»:
 * копирование в браузере разрешено не всегда и не везде срабатывает, а человеку, который
 * не видит экрана, ссылку надо ещё и прочитать вслух. Ключ виден один раз — сервер хранит
 * лишь его отпечаток, поэтому переспросить ту же ссылку нельзя, можно выпустить новую.
 */
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { createHttpGateway } from '@core/layout/http-gateway.js';
import { Button, Choice, Field, Notice, Switch } from '../ui/controls.js';
import { useT } from '../state/settings.js';
import { INVITE_ROLES } from '../net/api.js';
import type { Api, Channel, InviteRole } from '../net/api.js';

const ROLE_PHRASE = {
  staff: 'invite.role.staff',
  head: 'invite.role.head',
  guest: 'invite.role.guest',
  admin: 'invite.role.admin',
} as const;

/** Завести Канал. Открытый виден всем сотрудникам, закрытый — только добавленным. */
export function NewChannel({
  api,
  onCreated,
}: {
  readonly api: Api;
  /** `ready` — встало ли в новом Канале место для переписки. */
  readonly onCreated: (channel: Channel, ready: boolean) => void;
}): ReactNode {
  const t = useT();
  const [name, setName] = useState('');
  const [closed, setClosed] = useState(false);
  const [working, setWorking] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!name.trim()) {
      setRefusal(t('channels.needName'));
      return;
    }
    setWorking(true);
    setRefusal(null);
    try {
      const channel = await api.createChannel({
        name: name.trim(),
        kind: closed ? 'private' : 'open',
      });
      // Канал заводят, чтобы в нём переписываться, — поэтому место для переписки ставим
      // сразу. Пустая Раскладка выглядит поломкой: Канал есть, а написать в нём некуда,
      // пока Администратор не догадается собрать её отдельной кнопкой.
      let ready = true;
      try {
        await createHttpGateway({
          baseUrl: '',
          spaceSlug: api.spaceSlug,
          owner: { kind: 'channel', channelId: channel.id },
        }).addBlock({ kind: 'conversation', position: { col: 1, row: 1, width: 6, height: 8 } });
      } catch {
        // Не встало — Канал всё равно заведён, и Раскладку можно собрать руками. Ронять
        // из-за этого всю затею значило бы отнять у человека сделанную работу, но и молчать
        // нельзя: он увидит Канал без единого Блока и решит, что тот сломан.
        ready = false;
      }
      setName('');
      setClosed(false);
      onCreated(channel, ready);
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : t('common.serverRefused'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 rounded-md border border-line p-3">
      <h3 className="text-sm font-semibold">{t('channels.createTitle')}</h3>
      <Field
        label={t('channels.name')}
        value={name}
        onChange={(event) => setName(event.currentTarget.value)}
      />
      <Switch label={t('channels.kindPrivate')} checked={closed} onToggle={setClosed} />
      {refusal ? <Notice kind="error">{refusal}</Notice> : null}
      <Button type="submit" look="primary" disabled={working} className="justify-center">
        {working ? t('common.saving') : t('channels.submit')}
      </Button>
    </form>
  );
}

/** Позвать человека: почта в Список разрешённых, оттуда — одноразовая ссылка. */
export function InvitePerson({ api }: { readonly api: Api }): ReactNode {
  const t = useT();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('staff');
  const [working, setWorking] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!email.trim()) {
      setRefusal(t('invite.needEmail'));
      return;
    }
    setWorking(true);
    setRefusal(null);
    setCopied(false);
    try {
      const invitation = await api.invite({ email: email.trim(), role });
      // Сервер отдаёт путь без имени сайта: он не знает, каким адресом к нему пришли.
      setLink(`${window.location.origin}${invitation.path}`);
      setEmail('');
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : t('common.serverRefused'));
    } finally {
      setWorking(false);
    }
  };

  const copy = async (): Promise<void> => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      // Копирование запрещено настройками браузера — ссылка всё равно на экране текстом,
      // её можно выделить руками. Пугать человека отказом здесь не за что.
      setCopied(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 rounded-md border border-line p-3">
      <h3 className="text-sm font-semibold">{t('invite.title')}</h3>
      <Field
        label={t('invite.email')}
        type="email"
        value={email}
        autoComplete="off"
        onChange={(event) => setEmail(event.currentTarget.value)}
      />
      <Choice
        label={t('invite.role')}
        value={role}
        onChange={(event) => setRole(event.currentTarget.value as InviteRole)}
      >
        {INVITE_ROLES.map((one) => (
          <option key={one} value={one}>
            {t(ROLE_PHRASE[one])}
          </option>
        ))}
      </Choice>

      {refusal ? <Notice kind="error">{refusal}</Notice> : null}

      <Button type="submit" look="primary" disabled={working} className="justify-center">
        {working ? t('invite.working') : t('invite.submit')}
      </Button>

      {link ? (
        <div className="flex flex-col gap-2">
          <Notice>{t('invite.ready')}</Notice>
          <Field label={t('invite.linkLabel')} value={link} readOnly onFocus={(event) => event.currentTarget.select()} />
          <Button onClick={() => void copy()}>{copied ? t('invite.copied') : t('invite.copy')}</Button>
        </div>
      ) : null}
    </form>
  );
}
